/* eslint-disable react/prop-types */
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import React from 'react'

import {
  followUpRequiredByTopics,
  followUpRequiredReason
} from 'app/pages/Communication/InteractionHistory/templates'

// A custom BaseForm widget: first ASKS "is a follow-up required?" (Yes/No);
// only when the team picks YES does a date picker appear, and then the date is
// MANDATORY. BOTH the answer and (on Yes) the date are required to save.
// The value stored on CrmInteraction.follow_up_date encodes it:
//   '' / undefined  -> not answered yet              => INVALID
//   null            -> No follow-up                   => valid
//   'yes'           -> Yes, but no date chosen yet    => INVALID
//   'YYYY-MM-DD'    -> Yes + date                     => valid
// '' is what BaseTable seeds every new form field with, so a brand-new
// interaction starts UNANSWERED (neither radio selected) rather than defaulting
// to No. The Interactions page's beforeSaveFunction checks these sentinels to
// pop a toast and block the save; here we show an immediate inline hint too.
// Only 'YYYY-MM-DD' is ever sent to the API (see api/communication.js).
//
// A Recharge "Will recharge" answer of Yes / Undecided makes the DATE
// compulsory (user 2026-09-24, see followUpRequiredByTopics): "No" is disabled
// and a hint says why. The widget reads that off the surrounding Formik form
// itself, because the Interactions page hosts it through BaseForm, which has no
// way to pass it live form values. The save hooks enforce the same rule — this
// is only the guidance.
const YES_PENDING = 'yes'
const isDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)
const fmt = (d) => dayjs(d).format('YYYY-MM-DD')
const pretty = (d) => dayjs(d).format('DD MMM YYYY')

const FollowUpWidget = ({ fieldName, setFieldValue, formikFieldData, label }) => {
  const value = formikFieldData

  // Derive the Yes/No answer + the date from the stored value. Unanswered is a
  // distinct state from No — neither radio is selected until they pick one.
  const isAnswered = value === null || value === YES_PENDING || isDate(value)
  const isYes = value === YES_PENDING || isDate(value)
  const dateValue = isDate(value) ? value : ''
  const missingAnswer = !isAnswered
  const missingDate = isYes && !dateValue
  const today = fmt(dayjs())
  const formik = useFormikContext()
  const requiredBy = followUpRequiredByTopics(formik?.values)

  const set = (v) => setFieldValue(fieldName, v)

  const onAnswer = (ans) => {
    if (ans === 'no') set(null)
    // Switching to Yes: keep an existing date, else drop to the pending
    // sentinel so the "date required" hint shows until they pick one.
    else set(dateValue || YES_PENDING)
  }

  return (
    <Box my={1}>
      <Typography variant="caption" color={missingAnswer ? 'error' : 'textSecondary'}>
        {`${label || 'Follow-up required?'}*`}
      </Typography>
      <RadioGroup
        row
        value={isAnswered ? (isYes ? 'yes' : 'no') : ''}
        onChange={(e) => onAnswer(e.target.value)}
      >
        <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
        <FormControlLabel
          value="no"
          control={<Radio size="small" />}
          label="No"
          disabled={Boolean(requiredBy)}
        />
      </RadioGroup>
      {requiredBy && !dateValue ? (
        <FormHelperText error>
          {`Follow-up date required — ${followUpRequiredReason(requiredBy)}`}
        </FormHelperText>
      ) : null}
      {missingAnswer ? (
        <FormHelperText error>
          Please answer whether a follow-up is required
        </FormHelperText>
      ) : null}

      {isYes ? (
        <Box mt={0.5}>
          <Box style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              type="date"
              size="small"
              value={dateValue}
              inputProps={{ min: today }}
              onChange={(e) => set(e.target.value || YES_PENDING)}
              error={missingDate}
              style={{ marginRight: 8 }}
            />
          </Box>
          {missingDate ? (
            <FormHelperText error>Please choose a follow-up date</FormHelperText>
          ) : (
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              style={{ marginTop: 4 }}
            >
              Follow up by {pretty(dateValue)}
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  )
}

export default FollowUpWidget
